// Remove CheckIcon from imports if it's not being used
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { 
  DocumentArrowUpIcon, 
  DocumentTextIcon, 
  TrashIcon, 
  ArrowLeftIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const EventPdfUpload = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useContext(AuthContext);
  
  // États
  const [event, setEvent] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pdfToDelete, setPdfToDelete] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Chargement des données de l'événement et des PDFs associés
  useEffect(() => {
    // 1) On vide tout ce qui concerne l'ancien événement
    setPdfs([]);
    setError('');
    setSuccess('');
    setLoading(true);
  
    // 2) Puis on recharge les données (événement + PDFs)
    const fetchEventData = async () => {
      try {
        // Récupérer les détails de l'événement
        const eventResponse = await api.get(`/events/${id}`);
        setEvent(eventResponse.data.data.event);
  
        // Récupérer les PDFs associés à l'événement
        const pdfsResponse = await api.get(`/events/${id}/pdfs`);
        setPdfs(pdfsResponse.data.data.pdfs || []);
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
  
    fetchEventData();
  }, [id, api]);

  // Gestion de la sélection de fichier
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };
  
  // Validation du fichier
  const validateAndSetFile = (file) => {
    if (file.type !== 'application/pdf') {
      setError('Seuls les fichiers PDF sont acceptés');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) { // 20MB
      setError('La taille du fichier ne doit pas dépasser 20MB');
      return;
    }
    
    setSelectedFile(file);
    setError('');
  };

  // Gestion du drag & drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  // Gestion de l'upload du fichier
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    try {
      setUploadProgress(0);
      setUploadingPdf(true);
      const formData = new FormData();
      formData.append('pdfs', selectedFile);

      const response = await api.post(`/events/${id}/pdfs`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      // Ajouter le nouveau PDF à la liste
      setPdfs([...pdfs, response.data.data.pdf]);
      setSelectedFile(null);
      setSuccess('PDF ajouté avec succès');
      
      // Réinitialiser le champ de fichier
      document.getElementById('pdf-upload').value = '';
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de l\'upload:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'upload du fichier');
    } finally {
      setUploadProgress(0);
      setUploadingPdf(false);
    }
  };

  // Confirmation de suppression d'un PDF
  const confirmDelete = (pdf) => {
    setPdfToDelete(pdf);
    setShowDeleteConfirm(true);
  };

  // Suppression d'un PDF
  const handleDelete = async () => {
    if (!pdfToDelete) return;
    
    try {
      await api.delete(`/events/pdfs/${pdfToDelete.id}`);
      
      // Mettre à jour la liste des PDFs
      setPdfs(pdfs.filter(pdf => pdf.id !== pdfToDelete.id));
      setSuccess('PDF supprimé avec succès');
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression du fichier');
    } finally {
      setShowDeleteConfirm(false);
      setPdfToDelete(null);
    }
  };

  // Prévisualisation d'un PDF
  const handlePreview = (pdfUrl) => {
    window.open(pdfUrl, '_blank');
  };
  
  // État pour l'upload en cours
  const [uploadingPdf, setUploadingPdf] = useState(false);

  return (
    <div className="max-w-4xl mx-auto relative">
      {/* Image de théâtre floutée en arrière-plan */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-25 z-0" 
        style={{ 
          backgroundImage: 'url("/images/theatre-background.jpg")', 
          filter: 'blur(2px)',
          top: '64px' // Commence après la navbar
        }}
      ></div>
      
      {/* En-tête */}
      <div className="bg-white/90 rounded-xl shadow-lg overflow-hidden mb-8 border-t-4 border-t-gold relative z-10">
        <div className="bg-gradient-to-r from-primary/90 to-accent/90 text-white p-6">
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-4 border-white shadow-lg mr-6 flex items-center justify-center">
              <DocumentTextIcon className="h-12 w-12 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Gestion des documents</h1>
              {event && (
                <p className="text-white/80 mt-1">{event.title} - {new Date(event.start).toLocaleDateString('fr-FR')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages de succès et d'erreur */}
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded shadow-md relative z-10 flex items-center">
          <CheckIcon className="h-6 w-6 text-green-500 mr-3" />
          <p>{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-md relative z-10">
          <div className="flex">
            <div className="py-1">
              <XMarkIcon className="h-6 w-6 text-red-500 mr-3" />
            </div>
            <div>
              <p className="font-bold">Erreur</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="bg-white/90 rounded-xl shadow-lg p-6 relative z-10">
        {/* Bouton de retour */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-primary hover:text-accent mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour au calendrier
        </button>
        
        {/* Section d'upload */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-primary">Ajouter un nouveau document</h2>
          
          {/* Zone de drag & drop */}
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
              dragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <DocumentArrowUpIcon className={`h-12 w-12 mx-auto mb-4 ${dragActive ? 'text-primary' : 'text-gray-400'}`} />
            
            <p className="text-gray-600 mb-2">
              Glissez et déposez votre fichier PDF ici, ou
            </p>
            
            <label className="inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-accent cursor-pointer transition-colors">
              Parcourir
              <input
                id="pdf-upload"
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            
            {selectedFile && (
              <div className="mt-4 text-left bg-gray-50 p-3 rounded-md">
                <p className="font-medium text-gray-700 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-primary" />
                  {selectedFile.name}
                </p>
                <p className="text-sm text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
          </div>
          
          {/* Barre de progression */}
          {uploadProgress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div 
                className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
          
          {/* Bouton d'upload */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploadingPdf}
            className={`w-full py-2 rounded-md transition-colors ${
              !selectedFile || uploadingPdf
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-accent'
            }`}
          >
            {uploadingPdf ? 'Téléchargement en cours...' : 'Télécharger le document'}
          </button>
        </div>
        
        {/* Liste des PDFs */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-primary">Documents associés à l'événement</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : pdfs.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">Aucun document n'a été ajouté à cet événement</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {pdfs.map((pdf) => (
                <div 
                  key={pdf.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                >
                  <div className="p-4 flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">{pdf.filename}</h3>
                      <p className="text-sm text-gray-500">
                        Ajouté le {new Date(pdf.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePreview(pdf.url)}
                        className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                        title="Prévisualiser"
                      >
                        <DocumentTextIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => confirmDelete(pdf)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900">Confirmer la suppression</h3>
            <p className="mb-6 text-gray-600">
              Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPdfUpload;
